import isEmpty from 'lodash/isEmpty';
import { arrayOf, bool, func } from 'prop-types';
import React from 'react';

import { allStatusesEnum as invoiceStatuses } from '../../balance/constants';
import ListInReviewInvoices from '../../balance/invoices/ApprovableInvoices/ListInReviewInvoices';
import ExportInvoicesButton from '../../balance/invoices/ExportInvoicesButton';
import PushToSAPButton from '../../balance/invoices/PushToSAPButton';
import ListCategorizedCampaigns from '../../campaigns/ListCategorizedCampaigns';
import Tabs from '../../generic/Tabs';
import ListCategorizedInvoices from '../../invoices/ListCategorizedInvoices';
import ListCategorizedOpportunities from '../../opportunities/ListCategorizedOpportunities';
import { invoiceShape, sessionShape } from '../../prop-types';
import api from '../../REST/api';
import withApiRequest from '../../REST/with-api-request';
import withResolved from '../../REST/with-resolved';
import withSessionInfo from '../../signin/with-session-info';
import replaceById from '../../utils/replace-by-id';

const inReviewInvoices = invoices => {
  return invoices.filter(i => i.status === invoiceStatuses.IN_REVIEW.value);
};

const approvedInvoices = invoices => {
  return invoices.filter(i => i.status === invoiceStatuses.APPROVED.value);
};

class AccountingDashboard extends React.Component {
  constructor(props) {
    super(props);

    this.setApprovedToExported = this.setApprovedToExported.bind(this);
    this.handleUpdateStatus = this.handleUpdateStatus.bind(this);
    this.updateInReviewInvoice = this.updateInReviewInvoice.bind(this);
    this.regroupInvoices = this.regroupInvoices.bind(this);
    this.state = {
      inReviewInvoices: inReviewInvoices(this.props.invoices),
      approvedInvoices: approvedInvoices(this.props.invoices),
      selectedInvoices: [],
    };
  }

  handleUpdateStatus(invoiceId, newStatus, message = undefined) {
    const { onRequest } = this.props;
    const data = { status: newStatus };
    if (message) {
      data.requestedChanges = message;
    }

    return onRequest({
      id: invoiceId,
      data,
    }).then(response => this.updateInReviewInvoice(invoiceId, response));
  }

  getSelectedItems = items => this.setState({ selectedInvoices: items });

  setApprovedToExported() {
    this.setState({
      approvedInvoices: [],
    });
  }

  reloadPage = () => window.location.reload();

  regroupInvoices() {
    const invoices = [
      ...this.state.inReviewInvoices,
      ...this.state.approvedInvoices,
    ];
    this.setState({
      inReviewInvoices: inReviewInvoices(invoices),
      approvedInvoices: approvedInvoices(invoices),
    });
  }

  approvedInvoicesExist() {
    return [
      ...this.state.approvedInvoices,
      ...this.state.inReviewInvoices,
    ].some(i => i.status === invoiceStatuses.APPROVED.value);
  }

  updateInReviewInvoice(invoiceId, response) {
    const updatedInvoice = this.state.inReviewInvoices.find(
      i => i.id === invoiceId,
    );
    updatedInvoice.status = response.status;
    updatedInvoice.requestedChanges = response.requestedChanges;

    this.setState(prevState => ({
      inReviewInvoices: replaceById(prevState.inReviewInvoices, updatedInvoice),
    }));
  }

  tabs() {
    const reviewInvoicesView = (
      <ListInReviewInvoices
        data={this.state.inReviewInvoices}
        onUpdateStatus={this.handleUpdateStatus}
        loading={this.props.loading}
      />
    );

    const invoicesView = (
      <ListCategorizedInvoices
        // adding key based on the approvedInvoices state since when the export or push buttons are clicked
        // we want this component to get mounted again (without the key it would just re render without the HOC func
        // not being called)
        key={this.state.approvedInvoices.length}
        title=""
        showOnlyAccountingStatuses
        setSelectedItems={this.getSelectedItems}
        allowSelection
      />
    );

    const campaignsView = <ListCategorizedCampaigns title="" />;

    const opportunitiesView = (
      <ListCategorizedOpportunities title="" showCreateLink={false} />
    );

    return [
      {
        label: 'Review Invoices',
        value: 'Review Invoices',
        content: reviewInvoicesView,
      },
      {
        label: 'Other Invoices',
        value: 'Other Invoices',
        content: invoicesView,
      },
      {
        label: 'Campaigns',
        value: 'Campaigns',
        content: campaignsView,
      },
      {
        label: 'Opportunities',
        value: 'Opportunities',
        content: opportunitiesView,
      },
    ];
  }

  render() {
    const { selectedInvoices } = this.state;
    const { session } = this.props;
    if (!(session && session.user)) return null; // user info not yet loaded

    return (
      <Tabs
        tabs={this.tabs()}
        onTabChange={this.regroupInvoices}
        defaultActive={
          isEmpty(this.state.inReviewInvoices)
            ? 'Other Invoices'
            : 'Review Invoices'
        }
        id="accountingDashboard"
      >
        <ExportInvoicesButton
          disabled={!this.approvedInvoicesExist()}
          onSuccess={this.setApprovedToExported}
        />
        <PushToSAPButton
          disabled={!this.approvedInvoicesExist()}
          onSuccess={this.setApprovedToExported}
          className="push-all-invoices-button ml-2"
        />
        <PushToSAPButton
          disabled={!selectedInvoices.length}
          onSuccess={this.reloadPage}
          className="push-selected-invoices-button ml-2"
          selectedInvoices={selectedInvoices}
        />
      </Tabs>
    );
  }
}

AccountingDashboard.propTypes = {
  invoices: arrayOf(invoiceShape.isRequired).isRequired,
  onRequest: func.isRequired,
  loading: bool.isRequired,
  session: sessionShape,
};

AccountingDashboard.defaultProps = {
  session: null,
};

const resolvedAccountingDashboard = withResolved({
  query: api.getInvoices,
  deriveQueryArgs: () => ({
    search: {
      status: [invoiceStatuses.IN_REVIEW.value, invoiceStatuses.APPROVED.value],
    },
  }),
  as: 'invoices',
})(AccountingDashboard);

const AccountDashboardWithRequest = withApiRequest({
  query: api.patchInvoice,
  error: 'flash',
  successMessage: 'Invoice updated successfully',
})(resolvedAccountingDashboard);

export default withSessionInfo(AccountDashboardWithRequest);
